import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Database, FileText, Search, X } from 'lucide-react';
import { fieldClass, ghostBtn, primaryBtn } from '../components/GatewayUi';
import { logGovernedMcp } from '../governedMcpLogger';
import type { WorkspaceAssetCatalog, WorkspacePublishAsset } from '../governedMcpTypes';

function columnClass(isLight: boolean): string {
  return isLight
    ? 'rounded-xl border border-neutral-200 bg-white overflow-hidden min-h-0 h-44 md:h-full flex flex-col'
    : 'rounded-xl border border-neutral-800 bg-neutral-950 overflow-hidden min-h-0 h-44 md:h-full flex flex-col';
}

function rowClass(isLight: boolean, selected: boolean): string {
  if (selected) {
    return isLight
      ? 'w-full text-left px-3 py-2.5 text-sm bg-indigo-50 text-indigo-700'
      : 'w-full text-left px-3 py-2.5 text-sm bg-indigo-500/15 text-indigo-200';
  }
  return isLight
    ? 'w-full text-left px-3 py-2.5 text-sm text-neutral-700 hover:bg-neutral-50'
    : 'w-full text-left px-3 py-2.5 text-sm text-neutral-200 hover:bg-neutral-900';
}

export default function AssetExplorerModal({
  open,
  catalog,
  selectedAssetId,
  isLight,
  onClose,
  onSelect,
}: {
  open: boolean;
  catalog: WorkspaceAssetCatalog;
  selectedAssetId: string;
  isLight: boolean;
  onClose: () => void;
  onSelect: (asset: WorkspacePublishAsset) => void;
}) {
  const [query, setQuery] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [modelId, setModelId] = useState('');
  const [assetId, setAssetId] = useState(selectedAssetId);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const current = catalog.assets.find((item) => item.id === selectedAssetId);
    setSourceId(current?.sourceId || '');
    setModelId(current?.modelId || '');
    setAssetId(selectedAssetId);
    logGovernedMcp('app', 'publish_asset_explorer_open', { selectedAssetId, assets: catalog.assets.length });
  }, [open, selectedAssetId, catalog.assets]);

  const q = query.trim().toLowerCase();
  const sources = useMemo(() => {
    return catalog.sources.filter((source) => {
      if (!q) return true;
      return `${source.name} ${source.connectorKey}`.toLowerCase().includes(q);
    });
  }, [catalog.sources, q]);

  const models = useMemo(() => {
    return catalog.models.filter((model) => {
      if (sourceId && !model.sourceIds.includes(sourceId) && !catalog.assets.some((asset) => asset.modelId === model.id && asset.sourceId === sourceId)) {
        return false;
      }
      if (!q) return true;
      return `${model.name} ${model.spaceName}`.toLowerCase().includes(q);
    });
  }, [catalog.assets, catalog.models, q, sourceId]);

  const assets = useMemo(() => {
    return catalog.assets.filter((asset) => {
      if (sourceId && asset.sourceId !== sourceId) return false;
      if (modelId && asset.modelId !== modelId) return false;
      if (!q) return true;
      return `${asset.name} ${asset.modelName} ${asset.sourceName} ${asset.assetKind}`.toLowerCase().includes(q);
    });
  }, [catalog.assets, modelId, q, sourceId]);

  const pending = assets.find((item) => item.id === assetId) || catalog.assets.find((item) => item.id === assetId);

  if (!open) return null;

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const panel = isLight ? 'bg-white text-neutral-900' : 'bg-neutral-900 text-white';

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-black/40" aria-label="Close asset explorer" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="asset-explorer-title"
        className={`relative flex flex-col w-full max-w-5xl max-h-[min(40rem,calc(100dvh-2rem))] overflow-hidden rounded-2xl shadow-2xl ${panel}`}
      >
        <div className="shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-b border-black/5">
          <div>
            <h3 id="asset-explorer-title" className="text-base font-semibold">Choose an asset</h3>
            <p className={`text-xs mt-0.5 ${muted}`}>Explore source → data model → asset. One asset becomes this tool.</p>
          </div>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            <X size={16} />
          </button>
        </div>
        <div className="shrink-0 px-5 pt-4">
          <div className="relative">
            <Search size={15} className={`absolute left-3 top-1/2 -translate-y-1/2 ${muted}`} />
            <input
              className={`${fieldClass(isLight)} pl-9`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search sources, models, or assets"
            />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3 p-5 min-h-0 flex-1 overflow-y-auto md:overflow-hidden">
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Source</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              <button type="button" className={rowClass(isLight, !sourceId)} onClick={() => setSourceId('')}>
                All sources
              </button>
              {sources.map((source) => (
                <button
                  key={source.id}
                  type="button"
                  className={rowClass(isLight, sourceId === source.id)}
                  onClick={() => {
                    setSourceId(source.id);
                    setModelId('');
                    setAssetId('');
                  }}
                >
                  <span className="block truncate">{source.name}</span>
                  <span className={`block text-[11px] ${muted}`}>{source.connectorKey || 'connection'}</span>
                </button>
              ))}
            </div>
          </section>
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Data model</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              {models.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  className={rowClass(isLight, modelId === model.id)}
                  onClick={() => {
                    setModelId(model.id);
                    setAssetId('');
                  }}
                >
                  <span className="block truncate">{model.name}</span>
                  <span className={`block text-[11px] ${muted}`}>{model.spaceName || 'WorkSpace'}</span>
                </button>
              ))}
              {!models.length ? <p className={`px-3 py-6 text-sm ${muted}`}>No data models for this source.</p> : null}
            </div>
          </section>
          <section className={columnClass(isLight)}>
            <p className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide ${muted}`}>Asset</p>
            <div className="overflow-y-auto min-h-0 flex-1">
              {assets.map((asset) => {
                const isFile = asset.assetKind.toLowerCase() === 'file';
                return (
                  <button
                    key={asset.id}
                    type="button"
                    className={rowClass(isLight, assetId === asset.id)}
                    onClick={() => {
                      setModelId(asset.modelId);
                      setSourceId(asset.sourceId || sourceId);
                      setAssetId(asset.id);
                    }}
                  >
                    <span className="flex items-center gap-2">
                      {isFile ? <FileText size={14} /> : <Database size={14} />}
                      <span className="truncate">{asset.name}</span>
                    </span>
                    <span className={`block text-[11px] ${muted}`}>
                      {asset.modelName} · {isFile ? 'File' : 'Table'}
                    </span>
                  </button>
                );
              })}
              {!assets.length ? <p className={`px-3 py-6 text-sm ${muted}`}>No assets in this model.</p> : null}
            </div>
          </section>
        </div>
        <div className={`shrink-0 flex items-center justify-between gap-3 px-5 py-4 border-t ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
          <p className={`text-sm min-w-0 truncate ${muted}`}>
            {pending ? `${pending.modelName} → ${pending.name}` : 'Select an asset to continue'}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={primaryBtn()}
              disabled={!pending}
              onClick={() => {
                if (!pending) return;
                onSelect(pending);
                logGovernedMcp('app', 'publish_asset_selected', {
                  assetId: pending.id,
                  modelId: pending.modelId,
                  kind: pending.assetKind,
                });
              }}
            >
              <Check size={14} /> Use asset
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
